import React, { useState, useEffect, useRef } from "react";
import EditSheet from "./EditSheet";
import AddTaskSheet from "./AddTaskSheet";

const TYPE_OPTIONS = ["To Do", "Someday", "Reminder"];
const TAG_OPTIONS = ["Email", "Call", "Pay", "Schedule", "Sign Up"];
const PRIORITY_ORDER = { High: 0, Medium: 1, Low: 2 };

const TAG_COLORS = {
  Email: { bg: "#F1F1EF", text: "#787774" },
  Call: { bg: "#DDEDEA", text: "#2B8A3E" },
  Pay: { bg: "#EEE0DA", text: "#8C5B3F" },
  Schedule: { bg: "#EAE4F2", text: "#6940A5" },
  "Sign Up": { bg: "#FBF3DB", text: "#9F6B00" },
};

const DEFAULT_SETS = {
  reminders: new Set(["Reminder"]),
  tasks: new Set(["To Do"]),
  all: new Set(TYPE_OPTIONS),
};

function getInitialTypes(defaultView) {
  return new Set(DEFAULT_SETS[defaultView] || DEFAULT_SETS.all);
}

function formatDate(dateStr) {
  if (!dateStr) return { label: "No due date", soon: false };
  const today = new Date();
  const date = new Date(dateStr + "T00:00:00");
  const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: dateStr, soon: true };
  if (diffDays === 0) return { label: "Today", soon: true };
  if (diffDays === 1) return { label: "Tomorrow", soon: true };
  if (diffDays <= 7) return { label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }), soon: true };
  return { label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }), soon: false };
}

async function updateTask(pageId, action, value) {
  await fetch("/.netlify/functions/notion", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pageId, action, value }),
  });
}

function SkeletonRow() {
  return (
    <div className="skeleton-row">
      <div className="skeleton skeleton-circle" />
      <div className="skeleton-lines">
        <div className="skeleton skeleton-line-long" />
        <div className="skeleton skeleton-line-short" />
      </div>
    </div>
  );
}

export default function AllTasksPage({ onBack, defaultView = "all", onTabTap }) {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState(() => getInitialTypes(defaultView));
  const [selectedTags, setSelectedTags] = useState(() => new Set(TAG_OPTIONS));
  const [sort, setSort] = useState("date");
  const [editItem, setEditItem] = useState(null);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const menuRef = useRef(null);
  const tagMenuRef = useRef(null);

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowTypeMenu(false);
      if (tagMenuRef.current && !tagMenuRef.current.contains(e.target)) setShowTagMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch("/.netlify/functions/notion?type=all"),
        fetch("/.netlify/functions/notion?type=reminders_all"),
      ]);
      const [d1, d2] = await Promise.all([r1.json(), r2.json()]);
      const combined = [...(d1.items || []), ...(d2.items || [])];
      const seen = new Set();
      setAllItems(combined.filter(i => { if (seen.has(i.id)) return false; seen.add(i.id); return true; }));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCheck = async (e, id) => {
    e.stopPropagation();
    setAllItems(prev => prev.filter(i => i.id !== id));
    await updateTask(id, "toggleDone", true);
  };

  const handleStar = async (e, id, current) => {
    e.stopPropagation();
    const newVal = !current;
    setAllItems(prev => prev.map(i => i.id === id ? { ...i, isHighPriority: newVal } : i));
    await updateTask(id, "togglePriority", newVal);
  };

  const handleSave = (updated) => { setAllItems(prev => prev.map(i => i.id === updated.id ? updated : i)); setEditItem(null); };
  const handleDelete = (id) => { setAllItems(prev => prev.filter(i => i.id !== id)); setEditItem(null); };

  const handleTaskAdded = (item) => {
    setAllItems(prev => [item, ...prev]);
    // Make sure the new task's type is visible
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (item.type) next.add(item.type);
      return next;
    });
    setShowAddTask(false);
  };

  const toggleType = (t) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  };

  const toggleTag = (t) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  };

  const allChecked = selectedTypes.size === TYPE_OPTIONS.length;
  const noneChecked = selectedTypes.size === 0;
  const allTagsChecked = selectedTags.size === TAG_OPTIONS.length;
  const noTagsChecked = selectedTags.size === 0;

  let items = allItems.filter(i => {
    if (!selectedTypes.has(i.type)) return false;
    const itemTags = i.tag || [];
    if (itemTags.length === 0) return true;
    return itemTags.some(t => selectedTags.has(t));
  });
  items = [...items].sort((a, b) => {
    if (sort === "priority") {
      const pa = PRIORITY_ORDER[a.priority] ?? 3;
      const pb = PRIORITY_ORDER[b.priority] ?? 3;
      if (pa !== pb) return pa - pb;
    }
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return a.dueDate.localeCompare(b.dueDate);
  });

  const filterLabel = allChecked ? "All types" : noneChecked ? "None selected" : `${selectedTypes.size} selected`;
  const tagFilterLabel = allTagsChecked ? "All tags" : noTagsChecked ? "None selected" : `${selectedTags.size} selected`;

  return (
    <>
      <div className="app">
        <div style={{ background: "var(--white)", padding: "54px 20px 0", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)", flex: 1 }}>Tasks &amp; Reminders</span>
            <button onClick={() => setShowAddTask(true)} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--sage)", border: "none", fontSize: 20, color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>️</button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 10, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
            <div style={{ position: "relative", flex: 1 }} ref={menuRef}>
              <button onClick={() => setShowTypeMenu(v => !v)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: showTypeMenu ? "var(--sage)" : "var(--cream-hover)", border: "1.5px solid", borderColor: showTypeMenu ? "var(--sage)" : "var(--border)", borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: showTypeMenu ? "white" : "var(--charcoal)", fontFamily: "inherit", cursor: "pointer" }}>
                <span>{filterLabel}</span>
                <span style={{ fontSize: 10, marginLeft: 4 }}>{showTypeMenu ? "▲" : "▼"}</span>
              </button>
              {showTypeMenu && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", boxShadow: "0 4px 20px rgba(0,0,0,0.12)", zIndex: 300, overflow: "hidden" }}>
                  <div style={{ display: "flex", borderBottom: "1px solid var(--border)" }}>
                    <button onClick={() => setSelectedTypes(new Set(TYPE_OPTIONS))} style={{ flex: 1, padding: "10px 14px", background: "none", border: "none", borderRight: "1px solid var(--border)", fontSize: 12, fontWeight: 800, color: "var(--sage)", cursor: "pointer", fontFamily: "inherit" }}>Select all</button>
                    <button onClick={() => setSelectedTypes(new Set())} style={{ flex: 1, padding: "10px 14px", background: "none", border: "none", fontSize: 12, fontWeight: 800, color: "var(--rose)", cursor: "pointer", fontFamily: "inherit" }}>Deselect all</button>
                  </div>
                  {TYPE_OPTIONS.map(t => {
                    const checked = selectedTypes.has(t);
                    return (
                      <button key={t} onClick={() => toggleType(t)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: checked ? "var(--sage-muted)" : "none", border: "none", borderBottom: "1px solid var(--border)", fontSize: 13, fontWeight: 600, color: "var(--charcoal)", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                        <div style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, background: checked ? "var(--sage)" : "transparent", border: `2px solid ${checked ? "var(--sage)" : "var(--border)"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {checked && <span style={{ color: "white", fontSize: 12, fontWeight: 800 }}>✓</span>}
                        </div>
                        {t}
                      </button>
                    );
                  })}
                  <button onClick={() => setShowTypeMenu(false)} style={{ width: "100%", padding: "11px 14px", background: "var(--sage-muted)", border: "none", fontSize: 13, fontWeight: 800, color: "var(--sage)", cursor: "pointer", fontFamily: "inherit" }}>Done</button>
                </div>
              )}
            </div>
            <div style={{ position: "relative", flex: 1 }} ref={tagMenuRef}>
              <button onClick={() => setShowTagMenu(v => !v)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: showTagMenu ? "var(--sage)" : "var(--cream-hover)", border: "1.5px solid", borderColor: showTagMenu ? "var(--sage)" : "var(--border)", borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: showTagMenu ? "white" : "var(--charcoal)", fontFamily: "inherit", cursor: "pointer" }}>
                <span>{tagFilterLabel}</span>
                <span style={{ fontSize: 10, marginLeft: 4 }}>{showTagMenu ? "▲" : "▼"}</span>
              </button>
              {showTagMenu && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", boxShadow: "0 4px 20px rgba(0,0,0,0.12)", zIndex: 300, overflow: "hidden" }}>
                  <div style={{ display: "flex", borderBottom: "1px solid var(--border)" }}>
                    <button onClick={() => setSelectedTags(new Set(TAG_OPTIONS))} style={{ flex: 1, padding: "10px 14px", background: "none", border: "none", borderRight: "1px solid var(--border)", fontSize: 12, fontWeight: 800, color: "var(--sage)", cursor: "pointer", fontFamily: "inherit" }}>Select all</button>
                    <button onClick={() => setSelectedTags(new Set())} style={{ flex: 1, padding: "10px 14px", background: "none", border: "none", fontSize: 12, fontWeight: 800, color: "var(--rose)", cursor: "pointer", fontFamily: "inherit" }}>Deselect all</button>
                  </div>
                  {TAG_OPTIONS.map(t => {
                    const checked = selectedTags.has(t);
                    return (
                      <button key={t} onClick={() => toggleTag(t)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: checked ? "var(--sage-muted)" : "none", border: "none", borderBottom: "1px solid var(--border)", fontSize: 13, fontWeight: 600, color: "var(--charcoal)", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                        <div style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, background: checked ? "var(--sage)" : "transparent", border: `2px solid ${checked ? "var(--sage)" : "var(--border)"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {checked && <span style={{ color: "white", fontSize: 12, fontWeight: 800 }}>✓</span>}
                        </div>
                        {t}
                      </button>
                    );
                  })}
                  <button onClick={() => setShowTagMenu(false)} style={{ width: "100%", padding: "11px 14px", background: "var(--sage-muted)", border: "none", fontSize: 13, fontWeight: 800, color: "var(--sage)", cursor: "pointer", fontFamily: "inherit" }}>Done</button>
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, paddingBottom: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>Sort</span>
            <select value={sort} onChange={e => setSort(e.target.value)} style={{ appearance: "none", WebkitAppearance: "none", background: "var(--cream-hover)", border: "1.5px solid var(--border)", borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700, color: "var(--charcoal)", fontFamily: "inherit", cursor: "pointer" }}>
              <option value="date">Due Date</option>
              <option value="priority">Priority</option>
            </select>
          </div>
        </div>

        <div className="scroll-body">
          <div className="item-list">
            {loading ? (
              <><SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow /></>
            ) : items.length === 0 ? (
              <div className="empty-state"><div className="empty-icon">🎉</div><div className="empty-text">Nothing here!</div></div>
            ) : (
              items.map(item => {
                const d = formatDate(item.dueDate);
                const reminder = item.type === "Reminder";
                return (
                  <div key={item.id} className="task-row" onClick={() => setEditItem(item)}>
                    {reminder ? (
                      <div style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <div style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--sage)" }} />
                      </div>
                    ) : (
                      <button className="task-check" onClick={e => handleCheck(e, item.id)} aria-label="Complete" />
                    )}
                    <div className="task-content">
                      <div className="task-name">{item.name}</div>
                      <div className="task-sub">
                        <span className={`task-date${d.soon ? " soon" : ""}`}>{d.label}</span>
                        {item.type && item.type !== "To Do" && (<><span className="meta-dot" /><span className="type-pill">{item.type}</span></>)}
                        {item.priority && item.priority !== "Medium" && (<><span className="meta-dot" /><span className="type-pill">{item.priority}</span></>)}
                        {(item.tag || []).map(t => {
                          const c = TAG_COLORS[t] || { bg: "var(--cream-hover)", text: "var(--muted)" };
                          return (<React.Fragment key={t}><span className="meta-dot" /><span style={{ fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 20, background: c.bg, color: c.text }}>{t}</span></React.Fragment>);
                        })}
                      </div>
                    </div>
                    <button className={`task-star${item.isHighPriority ? " starred" : ""}`} onClick={e => handleStar(e, item.id, item.isHighPriority)} aria-label="Toggle priority">{item.isHighPriority ? "★" : "☆"}</button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", padding: "12px 20px 24px", textAlign: "center", fontSize: 13, fontWeight: 600, color: "var(--muted)" }}>
          {!loading && `${items.length} item${items.length !== 1 ? "s" : ""}`}
        </div>
      </div>

      <EditSheet item={editItem} onClose={() => setEditItem(null)} onSave={handleSave} onDelete={handleDelete} />
      {showAddTask && <AddTaskSheet onClose={() => setShowAddTask(false)} onAdd={handleTaskAdded} />}
    </>
  );
}
